import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useMemo } from 'react';

import {
  resetNavigationAction,
  setNavigationAction,
} from 'app/shared-components/store/headerSlice';
import PatientFileHeader from 'app/pages/Patients/pages/PatientsPage/components/PatientDrawer/PatientFileHeader';
import { Tabs } from 'app/shared-components/structure';

import PatientDrawer from '../components/PatientDrawer';
import useClinicOrder from '../hooks/useClinicOrder';
import PrescribingHeader from '../components/PrescribingHeader';
import { useClinicContext } from '../ClinicContext';
import useOrderMessageThread from '../hooks/useOrderMessageThread';

const tabs = [
  {
    label: 'Order Request',
    value: 'order-request',
    path: '/prescribing/:orderId',
  },
  {
    label: 'Order Messages',
    value: 'order-messages',
    path: '/prescribing/:orderId/messages',
  },
];

export default function OrderDetailsLayout({ children }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(({ auth }) => auth.user);
  const { selectedClinicId } = useClinicContext();
  const { orderId } = useParams();

  const [patientForDrawer, setPatientForDrawer] = useState(false);

  const selectedTab = location.pathname.endsWith('messages') ? 'order-messages' : 'order-request';

  const orderListSearch = location.state?.orderListSearch ?? '';

  useEffect(() => {
    dispatch(
      setNavigationAction({
        title: 'Prescribing',
        type: 'button',
        callback: () => navigate(`/prescribing${orderListSearch}`),
      })
    );

    return () => dispatch(resetNavigationAction());
  }, [dispatch, navigate, orderListSearch]);

  const { clinicOrder, isInitialLoading } = useClinicOrder(orderId);

  function handleTabChange(event, newValue) {
    const newPath = tabs.find((tab) => tab.value === newValue)?.path;

    if (newPath) {
      navigate(newPath.replace(':orderId', orderId), { replace: true, state: location.state });
    }
  }

  const { messageThread } = useOrderMessageThread(orderId);

  const tabsWithBadge = useMemo(() => {
    return tabs.map((tab) => {
      if (tab.value === 'order-messages') {
        return {
          ...tab,
          badge: messageThread?.unreadMessagesCount || undefined,
        };
      }
      return tab;
    });
  }, [messageThread?.unreadMessagesCount]);

  return (
    <Box className="grow flex flex-col bg-white overflow-hidden">
      <PrescribingHeader user={user} clinicId={selectedClinicId} />
      <Box className="p-12 flex justify-between items-center">
        <Box className="cursor-pointer" onClick={() => setPatientForDrawer(clinicOrder.patient)}>
          <PatientFileHeader patient={clinicOrder?.patient} isLoading={isInitialLoading} />
        </Box>
      </Box>
      <Tabs tabs={tabsWithBadge} selectedTab={selectedTab} handleChange={handleTabChange} />
      {children}
      <PatientDrawer
        patient={patientForDrawer}
        closePatientDrawer={() => setPatientForDrawer(null)}
      />
    </Box>
  );
}
